// Mandatory Occurrence Reporting (Section T) + Version control (Section B).
import { useEffect, useState } from "react";
import { Save, Loader2, ShieldAlert, FileClock, Send, GitBranch, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import { inputCls } from "./shared";
import { AiRewriteMenu } from "./SectionEditor";

const TRIAGE = ["Not started", "Under review", "Confirmed", "Not reportable"];

export function Mor({ rid, mor = {}, categories = [], canEdit, onChange, boilerplate }) {
    const [m, setM] = useState(() => ({ ...mor }));
    const [suggest, setSuggest] = useState([]);
    const [busy, setBusy] = useState(false);
    const [tplBusy, setTplBusy] = useState("");
    const hasTemplate = !!boilerplate?.narratives?.T;

    useEffect(() => { setM({ ...mor }); }, [mor]); // eslint-disable-line
    useEffect(() => { api.get(`/bsc/${rid}/mor/suggest-incidents`).then((r) => setSuggest(r.data)).catch(() => {}); }, [rid]);

    const toggleCat = (c) => setM((p) => ({ ...p, reportable_categories: (p.reportable_categories || []).includes(c) ? p.reportable_categories.filter((x) => x !== c) : [...(p.reportable_categories || []), c] }));
    const toggleInc = (id) => setM((p) => ({ ...p, linked_incident_ids: (p.linked_incident_ids || []).includes(id) ? p.linked_incident_ids.filter((x) => x !== id) : [...(p.linked_incident_ids || []), id] }));

    async function prefillFromTemplate() {
        if ((m.process_description || "").trim() && !window.confirm("Replace the current MOR process description with the standard template text?")) return;
        setTplBusy("prefill");
        try {
            const { data } = await api.get(`/bsc/${rid}/section/T/boilerplate-text`);
            setM((p) => ({ ...p, process_description: data.body }));
            toast.success("Pre-filled from template — review and save");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No template available"); }
        finally { setTplBusy(""); }
    }
    async function aiRewrite(mode) {
        setTplBusy(`rw-${mode}`);
        try {
            const { data } = await api.post(`/bsc/${rid}/section/T/boilerplate-rewrite`,
                { mode, field_key: null, text: m.process_description || "" });
            setM((p) => ({ ...p, process_description: data.draft }));
            toast.success("AI rewrite ready — review and save (not approved)");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI rewrite unavailable"); }
        finally { setTplBusy(""); }
    }

    async function save() {
        setBusy(true);
        try {
            await api.patch(`/bsc/${rid}/mor`, {
                process_description: m.process_description, reportable_categories: m.reportable_categories,
                linked_incident_ids: m.linked_incident_ids, triage_status: m.triage_status, confirm_mor: m.confirm_mor,
                bsr_notification_required: m.bsr_notification_required, bsr_notification_date: m.bsr_notification_date,
                bsr_notification_reference: m.bsr_notification_reference, notes: m.notes,
            });
            toast.success("MOR saved"); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }

    const [recipients, setRecipients] = useState((mor.escalation_recipients || []).join(", "));
    const [note, setNote] = useState("");
    const [notifying, setNotifying] = useState(false);
    async function notifyBsr() {
        const list = recipients.split(",").map((x) => x.trim()).filter(Boolean);
        if (!list.length) { toast.error("Enter at least one BSR recipient email"); return; }
        if (!window.confirm(`Escalate this Mandatory Occurrence to the BSR?\nA notification will be sent to: ${list.join(", ")}`)) return;
        setNotifying(true);
        try {
            await api.post(`/bsc/${rid}/mor/notify-bsr`, { recipients: list, note });
            toast.success("BSR notification sent & logged");
            setNote(""); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Notification failed"); }
        finally { setNotifying(false); }
    }
    const escLog = m.escalation_record || mor.escalation_record || [];

    return (
        <div data-testid="bscr-mor" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-[18px] font-bold text-[#1F1F1F] flex items-center gap-2"><ShieldAlert size={18} className="text-[#B33A2E]" /> Mandatory Occurrence Reporting</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Triage building-safety incidents and record BSR notification.</p>
                </div>
                {canEdit && <button data-testid="bscr-mor-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>}
            </div>

            <div className="rounded-xl border border-[#E5E0DA] bg-white p-4 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">MOR process description</label>
                    {canEdit && hasTemplate && (
                        <div className="flex items-center gap-2">
                            <button data-testid="bscr-mor-prefill" onClick={prefillFromTemplate} disabled={!!tplBusy}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-[#1F6FE5] border border-[#CFE3FB] bg-[#F2F8FF] disabled:opacity-60">
                                {tplBusy === "prefill" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Pre-fill from template
                            </button>
                            <AiRewriteMenu testid="bscr-mor-rewrite" busy={tplBusy.startsWith("rw-")} onPick={aiRewrite} />
                        </div>
                    )}
                </div>
                <textarea data-testid="bscr-mor-process" disabled={!canEdit} rows={4} value={m.process_description || ""} onChange={(e) => setM({ ...m, process_description: e.target.value })} className={inputCls} />
                {hasTemplate && <p className="text-[11px] text-[#9A968F]">Drafts are review-required — they fill the box for you to edit, then <b>Save</b>. Nothing is auto-approved.</p>}
            </div>

            <div className="rounded-xl border border-[#E5E0DA] bg-white p-4">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Reportable categories</label>
                <div className="grid sm:grid-cols-2 gap-1.5">
                    {categories.map((c) => (
                        <label key={c} className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" disabled={!canEdit} checked={(m.reportable_categories || []).includes(c)} onChange={() => toggleCat(c)} /> {c}</label>
                    ))}
                </div>
            </div>

            <div className="rounded-xl border border-[#E5E0DA] bg-white p-4">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Triage incidents ({suggest.length} candidate{suggest.length === 1 ? "" : "s"})</label>
                {suggest.length === 0 ? <p className="text-[12px] italic text-[#9A968F]">No RIDDOR / major / dangerous-occurrence incidents on this building.</p> : (
                    <div className="space-y-1.5">
                        {suggest.map((i) => (
                            <label key={i.id} data-testid={`bscr-mor-inc-${i.id}`} className="flex items-center gap-2.5 text-[12.5px] rounded-lg border border-[#EFEAE4] px-2.5 py-1.5">
                                <input type="checkbox" disabled={!canEdit} checked={(m.linked_incident_ids || []).includes(i.id)} onChange={() => toggleInc(i.id)} />
                                <span className="font-mono text-[11px] text-[#9A968F]">{i.reference}</span>
                                <span className="font-semibold">{i.incident_type}</span>
                                <Pill tone={i.severity === "Major" || i.severity === "Fatal" ? "red" : "amber"}>{i.severity}</Pill>
                                {i.riddor && <Pill tone="red">RIDDOR</Pill>}
                                <span className="text-[#9A968F] ml-auto">{(i.occurred_at || "").slice(0, 10)}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            <div className="rounded-xl border border-[#E5E0DA] bg-white p-4 grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Triage status</label>
                    <select data-testid="bscr-mor-triage" disabled={!canEdit} value={m.triage_status || "Not started"} onChange={(e) => setM({ ...m, triage_status: e.target.value })} className={inputCls}>{TRIAGE.map((t) => <option key={t}>{t}</option>)}</select>
                </div>
                <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" disabled={!canEdit} checked={!!m.confirm_mor} onChange={(e) => setM({ ...m, confirm_mor: e.target.checked })} /> Confirm MOR</label>
                    <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" disabled={!canEdit} checked={!!m.bsr_notification_required} onChange={(e) => setM({ ...m, bsr_notification_required: e.target.checked })} /> BSR notification required</label>
                </div>
                {m.bsr_notification_required && (<>
                    <div><label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">BSR notification date</label><input type="date" disabled={!canEdit} value={m.bsr_notification_date || ""} onChange={(e) => setM({ ...m, bsr_notification_date: e.target.value })} className={inputCls} /></div>
                    <div><label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">BSR reference</label><input disabled={!canEdit} value={m.bsr_notification_reference || ""} onChange={(e) => setM({ ...m, bsr_notification_reference: e.target.value })} className={inputCls} /></div>
                </>)}
            </div>

            {/* BSR escalation */}
            <div data-testid="bscr-mor-escalation" className="rounded-xl border-2 border-[#F1D9D4] bg-[#FCF6F4] p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Send size={15} className="text-[#B33A2E]" />
                    <h3 className="text-[13.5px] font-bold text-[#1F1F1F]">Escalate to the Building Safety Regulator</h3>
                </div>
                <p className="text-[12px] text-[#6F6A64]">Send a Mandatory Occurrence notification to the BSR / escalation contacts. Each notification is logged below with a timestamp.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">BSR recipient email(s)</label>
                        <input data-testid="bscr-mor-bsr-recipients" disabled={!canEdit} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="bsr@hse.gov.uk, escalation@…" className={inputCls} />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Escalation note (optional)</label>
                        <input data-testid="bscr-mor-bsr-note" disabled={!canEdit} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Context for the regulator…" className={inputCls} />
                    </div>
                </div>
                {canEdit && (
                    <button data-testid="bscr-mor-notify-bsr" onClick={notifyBsr} disabled={notifying || !m.confirm_mor}
                        title={!m.confirm_mor ? "Confirm the MOR before escalating" : undefined}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "#B33A2E" }}>
                        {notifying ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Notify BSR
                    </button>
                )}
                {!m.confirm_mor && <p className="text-[11.5px] italic text-[#9A968F]">Tick “Confirm MOR” above before escalating to the BSR.</p>}
                {escLog.length > 0 && (
                    <div data-testid="bscr-mor-escalation-log" className="mt-2 space-y-1.5">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Escalation log ({escLog.length})</div>
                        {escLog.slice().reverse().map((e) => (
                            <div key={e.id} className="text-[12px] rounded-lg border border-[#EFE2DF] bg-white px-2.5 py-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Pill tone="red">BSR notified</Pill>
                                    <span className="text-[#9A968F]">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                                    <span className="text-[#6F6A64]">→ {(e.recipients || []).join(", ")}</span>
                                </div>
                                <div className="text-[11.5px] text-[#6F6A64] mt-0.5">by {e.by || "—"}{e.note ? ` · ${e.note}` : ""}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function VersionHistory({ rid, record, canEdit, onChange }) {
    const versions = record.version_history || [];
    const status = record.status;
    async function issue() {
        if (!window.confirm("Issue this version? The current version will be locked.")) return;
        try { await api.post(`/bsc/${rid}/issue`, {}); toast.success("Report issued & locked"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function newVersion() {
        try { await api.post(`/bsc/${rid}/version`, {}); toast.success("New draft version created"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    return (
        <div data-testid="bscr-versions" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-[18px] font-bold text-[#1F1F1F] flex items-center gap-2"><FileClock size={18} /> Version Control</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Current version <b>{record.report_version}</b> · status {status}.</p>
                </div>
                {canEdit && (
                    <div className="flex gap-2">
                        {status !== "Issued" ? (
                            <button data-testid="bscr-issue" onClick={issue} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}><Send size={13} /> Issue report</button>
                        ) : (
                            <button data-testid="bscr-new-version" onClick={newVersion} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]"><GitBranch size={13} /> Start new version</button>
                        )}
                    </div>
                )}
            </div>
            <div className="rounded-xl border border-[#E5E0DA] bg-white overflow-hidden">
                <table className="w-full text-[12.5px]">
                    <thead><tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider text-[#6F6A64]"><th className="text-left px-4 py-2.5">Version</th><th className="text-left px-3 py-2.5">Author</th><th className="text-left px-3 py-2.5">Date</th><th className="text-left px-3 py-2.5">Changes</th><th className="text-left px-4 py-2.5">Status</th></tr></thead>
                    <tbody>
                        {versions.slice().reverse().map((v) => (
                            <tr key={v.id} className="border-t border-[#F0EDE8]">
                                <td className="px-4 py-2.5 font-semibold">{v.version}{v.locked && " 🔒"}</td>
                                <td className="px-3 py-2.5">{v.author || "—"}</td>
                                <td className="px-3 py-2.5">{(v.date || "").slice(0, 10)}</td>
                                <td className="px-3 py-2.5 text-[#6F6A64]">{v.changes}</td>
                                <td className="px-4 py-2.5"><Pill tone={v.review_status === "Issued" ? "green" : "neutral"}>{v.review_status}</Pill></td>
                            </tr>
                        ))}
                        {versions.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center italic text-[#9A968F]">No versions yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
